import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { MapPin, Users, Home, ArrowRight, Calendar, X, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Properties = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  
  // Get search parameters
  const checkIn = searchParams.get('checkIn');
  const checkOut = searchParams.get('checkOut');
  const guests = searchParams.get('guests');
  const destination = searchParams.get('destination') || 'all';

  const hasSearchParams = checkIn && checkOut && guests;

  const checkPropertiesAvailability = useCallback(async (propertiesToCheck) => {
    const availabilityChecks = propertiesToCheck.map(async (property) => {
      try {
        const response = await axios.post(`${API}/availability/check`, {
          property_id: property.id,
          check_in_date: checkIn,
          check_out_date: checkOut
        });
        
        return response.data.available ? property : null;
      } catch (error) {
        console.error(`Error checking availability for ${property.name}:`, error);
        return null;
      }
    });

    const results = await Promise.all(availabilityChecks);
    return results.filter(prop => prop !== null);
  }, [checkIn, checkOut]);

  const fetchAndFilterProperties = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/properties`);
      let allProperties = Array.isArray(response.data) ? response.data : [];

      if (destination !== 'all') {
        allProperties = allProperties.filter((prop) => {
          const location = prop.location.toLowerCase();
          if (destination === 'thessaloniki') {
            return location.includes('thessaloniki') || location.includes('θεσσαλονίκη');
          } else if (destination === 'halkidiki') {
            return location.includes('halkidiki') || location.includes('χαλκιδική') || location.includes('afytos');
          }
          return true;
        });
      }

      if (guests) {
        const guestCount = parseInt(guests);
        allProperties = allProperties.filter(prop => prop.max_guests >= guestCount);
      }

      setProperties(allProperties);

      if (hasSearchParams && checkIn && checkOut) {
        setCheckingAvailability(true);
        const availableProperties = await checkPropertiesAvailability(allProperties);
        setFilteredProperties(availableProperties);
        setCheckingAvailability(false);
      } else {
        setFilteredProperties(allProperties);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  }, [destination, guests, hasSearchParams, checkIn, checkOut, checkPropertiesAvailability]);

  useEffect(() => {
    fetchAndFilterProperties();
  }, [fetchAndFilterProperties]);

  const clearSearch = () => {
    setSearchParams({});
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  const getDestinationLabel = () => {
    if (destination === 'thessaloniki') {
      return i18n.language === 'el' ? 'Θεσσαλονίκη' : 'Thessaloniki';
    } else if (destination === 'halkidiki') {
      return i18n.language === 'el' ? 'Χαλκιδική' : 'Halkidiki';
    }
    return i18n.language === 'el' ? 'Όλα' : 'All';
  };

  return (
    <div className="bg-stone-50 min-h-screen py-16">
      <Helmet>
        <title>{i18n.language === 'el' ? 'Καταλύματα — InFox Suites' : 'Properties — InFox Suites'}</title>
        <meta name="description" content={i18n.language === 'el' ? 'Περιηγηθείτε σε όλα τα καταλύματα InFox Suites σε Θεσσαλονίκη και Χαλκιδική. Κρατήστε απευθείας.' : 'Browse all InFox Suites properties in Thessaloniki and Halkidiki. Book directly.'} />
        <meta property="og:title" content="Properties — InFox Suites" />
        <meta property="og:description" content="Browse all InFox Suites properties in Thessaloniki and Halkidiki." />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl md:text-6xl font-semibold mb-4">
            {t('nav.properties')}
          </h1>
          <p className="text-lg text-stone-600 font-light max-w-2xl mx-auto">
            {i18n.language === 'el' 
              ? 'Ανακαλύψτε τη συλλογή μας από premium καταλύματα στη Θεσσαλονίκη και τη Χαλκιδική'
              : 'Discover our collection of premium vacation rentals in Thessaloniki and Halkidiki'}
          </p>
        </div>

        {/* Search Summary */}
        {hasSearchParams && (
          <div className="mb-8 bg-white rounded-sm border border-stone-200 p-6 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center space-x-2 text-sm">
                  <MapPin className="w-4 h-4 text-primary" />
                  <span className="font-medium">{getDestinationLabel()}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <Calendar className="w-4 h-4 text-primary" />
                  <span>{formatDate(checkIn)} - {formatDate(checkOut)}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <Users className="w-4 h-4 text-primary" />
                  <span>{guests} {i18n.language === 'el' ? (parseInt(guests) === 1 ? 'Άτομο' : 'Άτομα') : (parseInt(guests) === 1 ? 'Guest' : 'Guests')}</span>
                </div>
              </div>
              <button
                onClick={clearSearch}
                className="flex items-center space-x-2 text-sm text-stone-600 hover:text-primary transition-colors"
              >
                <X className="w-4 h-4" />
                <span>{i18n.language === 'el' ? 'Καθαρισμός φίλτρων' : 'Clear filters'}</span>
              </button>
            </div>
            {checkingAvailability && (
              <div className="mt-4 flex items-center space-x-2 text-sm text-primary">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{i18n.language === 'el' ? 'Έλεγχος διαθεσιμότητας...' : 'Checking availability...'}</span>
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
            <div className="text-stone-500 text-lg">{t('home.loading')}</div>
          </div>
        ) : (
          <>
            {/* Results Count */}
            {hasSearchParams && !checkingAvailability && (
              <div className="mb-6 text-center">
                <p className="text-stone-600">
                  {filteredProperties.length > 0 ? (
                    <>
                      {i18n.language === 'el' 
                        ? `Βρέθηκαν ${filteredProperties.length} ${filteredProperties.length === 1 ? 'διαθέσιμο κατάλυμα' : 'διαθέσιμα καταλύματα'}`
                        : `Found ${filteredProperties.length} available ${filteredProperties.length === 1 ? 'property' : 'properties'}`}
                    </>
                  ) : (
                    <span className="text-stone-500">
                      {i18n.language === 'el'
                        ? 'Δεν βρέθηκαν διαθέσιμα καταλύματα για τις επιλεγμένες ημερομηνίες'
                        : 'No available properties for the selected dates'}
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Properties Grid */}
            {filteredProperties.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProperties.map((property, index) => {
                  const isLargeCard = index % 7 === 0 && !hasSearchParams;
                  return (
                    <Link
                      key={property.id}
                      to={`/properties/${property.slug}${hasSearchParams ? `?checkIn=${checkIn}&checkOut=${checkOut}&guests=${guests}` : ''}`}
                      className={`group bg-white rounded-sm border border-stone-200 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 ${
                        isLargeCard ? 'md:col-span-2 md:row-span-2' : ''
                      }`}
                      data-testid={`property-${property.slug}`}
                    >
                      <div className={`overflow-hidden bg-stone-100 ${
                        isLargeCard ? 'aspect-[16/9]' : 'aspect-[4/3]'
                      }`}>
                        <img
                          src={property.images[0]}
                          alt={property.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">
                          {property.name}
                        </h3>
                        <div className="space-y-2 mb-4 text-sm text-stone-600">
                          <p className="flex items-center">
                            <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                            {property.location}
                          </p>
                          <div className="flex items-center justify-between">
                            <p className="flex items-center">
                              <Users className="w-4 h-4 mr-2" />
                              {property.max_guests} {t('property.guests')}
                            </p>
                            <p className="flex items-center">
                              <Home className="w-4 h-4 mr-2" />
                              {property.size_sqm}m²
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between pt-4 border-t border-stone-200">
                          <div>
                            <span className="text-2xl font-semibold text-primary">
                              €{property.base_price_per_night}
                            </span>
                            <span className="text-sm text-stone-500 font-light ml-2">
                              {t('property.perNight')}
                            </span>
                          </div>
                          <div className="flex items-center text-primary group-hover:translate-x-1 transition-transform">
                            <span className="text-sm font-medium mr-1">{t('property.viewDetails')}</span>
                            <ArrowRight className="w-4 h-4" />
                          </div>
                        </div>
                        {property.available_until && (
                          <div className="mt-3 px-3 py-1 bg-amber-100 text-amber-800 text-xs rounded-sm inline-block">
                            {t('property.availableUntil')} {property.available_until}
                          </div>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : !loading && !checkingAvailability ? (
              <div className="text-center py-20">
                <div className="max-w-md mx-auto">
                  <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Home className="w-10 h-10 text-stone-400" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-3">
                    {i18n.language === 'el' ? 'Δεν βρέθηκαν διαθέσιμα καταλύματα' : 'No Available Properties'}
                  </h3>
                  <p className="text-stone-600 mb-8">
                    {i18n.language === 'el'
                      ? 'Δυστυχώς, δεν υπάρχουν διαθέσιμα καταλύματα για τις επιλεγμένες ημερομηνίες. Δοκιμάστε διαφορετικές ημερομηνίες ή προορισμό.'
                      : 'Unfortunately, there are no available properties for the selected dates. Try different dates or destination.'}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      onClick={clearSearch}
                      className="px-6 py-3 bg-primary text-white rounded-sm hover:bg-primary/90 transition-colors font-medium"
                    >
                      {i18n.language === 'el' ? 'Νέα Αναζήτηση' : 'New Search'}
                    </button>
                    <Link
                      to="/"
                      className="px-6 py-3 border border-stone-300 text-stone-700 rounded-sm hover:bg-stone-50 transition-colors font-medium"
                    >
                      {i18n.language === 'el' ? 'Επιστροφή στην Αρχική' : 'Back to Home'}
                    </Link>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default Properties;
